import React, { useState } from 'react';

const AuditTool: React.FC = () => {
    const [businessType, setBusinessType] = useState('');
    const [painPoint, setPainPoint] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setResult(null);

        // Determine API URL based on environment
        const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
        const API_URL = isProduction
            ? '../backend/routes/api/audit'
            : 'http://localhost:8000/api/audit';

        try {
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({ business_type: businessType, pain_point: painPoint }),
            });

            if (!response.ok) {
                // Try to parse error message from JSON if available
                let errorMessage = 'Failed to generate audit';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorMessage;
                } catch (e) {
                    // ignore json parse error
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();
            setResult(data.html);
        } catch (err: any) {
            setError(err.message || 'Something went wrong. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 flex flex-col items-center justify-center p-6 font-sans">
            <div className="w-full max-w-3xl bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-800 ring-1 ring-white/5">

                {/* Header Section */}
                <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-8 border-b border-slate-800">
                    <h1 className="text-4xl font-extrabold text-center mb-3 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 tracking-tight">
                        AlonForge Digital Audit
                    </h1>
                    <p className="text-slate-400 text-center text-lg font-light">
                        AI-Powered IT Strategy & Solutions
                    </p>
                </div>

                <div className="p-8 md:p-10">
                    {!result ? (
                        <form onSubmit={handleSubmit} className="space-y-8">
                            <div className="space-y-6">
                                <div className="group">
                                    <label htmlFor="businessType" className="block text-sm font-semibold text-cyan-400 mb-2 uppercase tracking-wider">
                                        Business Type
                                    </label>
                                    <input
                                        type="text"
                                        id="businessType"
                                        value={businessType}
                                        onChange={(e) => setBusinessType(e.target.value)}
                                        className="w-full px-5 py-4 bg-slate-950 border border-slate-700 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-white placeholder-slate-600 transition-all duration-200 ease-in-out group-hover:border-slate-600"
                                        placeholder="e.g. Retail Store, Law Firm, Coffee Shop"
                                        required
                                    />
                                </div>

                                <div className="group">
                                    <label htmlFor="painPoint" className="block text-sm font-semibold text-cyan-400 mb-2 uppercase tracking-wider">
                                        Main Challenge
                                    </label>
                                    <textarea
                                        id="painPoint"
                                        value={painPoint}
                                        onChange={(e) => setPainPoint(e.target.value)}
                                        className="w-full px-5 py-4 bg-slate-950 border border-slate-700 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-white placeholder-slate-600 h-40 transition-all duration-200 ease-in-out group-hover:border-slate-600 resize-none"
                                        placeholder="Describe your biggest operational headache..."
                                        required
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-full py-4 px-6 rounded-xl font-bold text-lg tracking-wide transition-all duration-300 transform hover:-translate-y-0.5 ${loading
                                    ? 'bg-slate-800 cursor-not-allowed text-slate-500 border border-slate-700'
                                    : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white shadow-lg shadow-cyan-900/50 border border-cyan-500/20'
                                    }`}
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center space-x-3">
                                        <svg className="animate-spin h-5 w-5 text-cyan-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>Generating Strategy...</span>
                                    </span>
                                ) : (
                                    'Analyze & Generate Plan'
                                )}
                            </button>
                        </form>
                    ) : (
                        <div className="animate-fadeIn space-y-8">
                            <div className="bg-slate-950/50 p-8 rounded-2xl border border-slate-800 shadow-inner">
                                <h2 className="text-2xl font-bold text-white mb-6 border-b border-slate-800 pb-4">
                                    Your Custom IT Strategy
                                </h2>
                                {/* Custom styling for the HTML content */}
                                <div
                                    className="prose-custom text-slate-300 space-y-4 leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: result }}
                                />
                                <style>{`
                  .prose-custom h3 {
                    color: #22d3ee; /* cyan-400 */
                    font-size: 1.25rem;
                    font-weight: 700;
                    margin-top: 1.5rem;
                    margin-bottom: 0.75rem;
                  }
                  .prose-custom ul {
                    list-style-type: disc;
                    padding-left: 1.5rem;
                    margin-bottom: 1.5rem;
                  }
                  .prose-custom li {
                    margin-bottom: 0.5rem;
                  }
                  .prose-custom p {
                    margin-bottom: 1rem;
                  }
                  .prose-custom strong {
                    color: #f8fafc; /* slate-50 */
                  }
                `}</style>
                            </div>

                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-800">
                                <button
                                    onClick={() => setResult(null)}
                                    className="text-slate-400 hover:text-cyan-400 transition-colors text-sm font-medium flex items-center"
                                >
                                    <span className="mr-2">←</span> Analyze Another Issue
                                </button>
                                <button className="w-full sm:w-auto bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold py-3 px-8 rounded-full shadow-lg shadow-cyan-500/20 transition-all transform hover:scale-105 active:scale-95">
                                    Book Free Consultation
                                </button>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="mt-6 p-4 bg-red-900/20 border border-red-500/30 rounded-xl flex items-center space-x-3 text-red-300">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuditTool;
